import {Box} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {LeasingRateMatrixCell} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {FinancingMatrixCell} from './FinancingMatrixCell';
import {MatrixCellTooltip} from './MatrixCellTooltip';
import {LeasingCellCoordinates} from './types/LeasingCellCoordinates';
import {buildMatrixCellAccessibleName} from './utils/buildMatrixCellAccessibleName';
import {formatCurrencyOrDash} from './utils/formatCurrencyOrDash';
import {formatMoney} from './utils/formatMoney';

interface LeasingMatrixRowCellProps {
  term: number;
  annualMileage: number;
  cell: LeasingRateMatrixCell | null;
  isActive: boolean;
  isReadOnly: boolean;
  onEditCell: (coordinates: LeasingCellCoordinates) => void;
}

/** One term × annual-mileage position of the leasing rate matrix. */
export function LeasingMatrixRowCell(props: LeasingMatrixRowCellProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const primaryValue = isNotNil(props.cell)
    ? formatMoney(formatCurrency, props.cell.monthlyRate)
    : undefined;
  const isConfigured = props.cell !== null;
  const actionLabel = match({isConfigured, isReadOnly: props.isReadOnly})
    .with({isConfigured: false}, () => i18n.t('general.actions.add'))
    .with({isConfigured: true, isReadOnly: true}, () =>
      i18n.t('page.vehicle.financingAndLeasing.configured')
    )
    .with({isConfigured: true, isReadOnly: false}, () =>
      i18n.t('page.vehicle.financingAndLeasing.editVariant')
    )
    .exhaustive();
  // A leasing rate is never inherited from a template, so no cell is ever `CUSTOM`.
  const cellState = match({isConfigured, isActive: props.isActive})
    .with({isConfigured: false}, () => 'EMPTY' as const)
    .with({isConfigured: true, isActive: true}, () => 'ACTIVE' as const)
    .with({isConfigured: true, isActive: false}, () => 'CONFIGURED' as const)
    .exhaustive();

  return (
    <Box paddingHorizontal={1} paddingTop={1}>
      <FinancingMatrixCell
        state={cellState}
        primaryValue={primaryValue}
        emptyLabel={i18n.t('general.actions.add')}
        ariaLabel={buildMatrixCellAccessibleName({
          action: actionLabel,
          coordinates: [
            {label: i18n.t('page.vehicle.financingAndLeasing.term'), value: props.term},
            {
              label: i18n.t('entity.vehicle.labels.annualMileage'),
              value: formatNumber(props.annualMileage),
            },
          ],
          statuses: [
            props.isActive ? i18n.t('page.vehicle.financingAndLeasing.activePrimary') : null,
            isNotNil(props.cell) ? i18n.t('page.vehicle.financingAndLeasing.configured') : null,
          ],
        })}
        isDisabled={props.isReadOnly && isNil(props.cell)}
        tooltip={
          isNotNil(props.cell) ? (
            <MatrixCellTooltip
              context={i18n.t('page.vehicle.financingAndLeasing.leasingCellContext', {
                term: props.term,
                annualMileage: formatNumber(props.annualMileage),
              })}
              headlineLabel={i18n.t('page.vehicle.financingAndLeasing.monthlyRateGross')}
              headlineValue={formatMoney(formatCurrency, props.cell.monthlyRate)}
              summary={i18n.t('page.vehicle.financingAndLeasing.grossRateHint')}
              values={[
                {
                  label: i18n.t('page.vehicle.financingAndLeasing.totalOverTerm'),
                  value: formatCurrencyOrDash(
                    formatCurrency,
                    Number(props.cell.monthlyRate.amount) * props.term,
                    props.cell.monthlyRate.currency
                  ),
                },
                ...(isNotNilOrEmpty(props.cell.nominalRate)
                  ? [
                      {
                        label: i18n.t('page.vehicle.financingAndLeasing.nominalRate'),
                        value: `${props.cell.nominalRate} %`,
                      },
                    ]
                  : []),
                ...(isNotNilOrEmpty(props.cell.apr)
                  ? [
                      {
                        label: i18n.t('page.vehicle.financingAndLeasing.apr'),
                        value: `${props.cell.apr} %`,
                      },
                    ]
                  : []),
              ]}
              statuses={[
                ...(props.isActive
                  ? [
                      {
                        label: i18n.t('page.vehicle.financingAndLeasing.activePrimary'),
                        state: 'ACTIVE' as const,
                      },
                    ]
                  : []),
                {
                  label: i18n.t('page.vehicle.financingAndLeasing.configured'),
                  state: 'CONFIGURED',
                },
              ]}
            />
          ) : undefined
        }
        onActivate={() => props.onEditCell({term: props.term, annualMileage: props.annualMileage})}
      />
    </Box>
  );
}
